/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: api/extensionsApi.ts
 🗓️ Created: 2025-01-15
 🧩 Description:
    API functions for Extensions module - all API calls centralized here.
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import type { Extension, ExtensionOptions } from '../types';

export interface ListParams {
  page: number;
  limit: number;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  search?: string;
  domainId?: number;
}

export interface ListResponse {
  data: Extension[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Fetches list of extensions with pagination and filters
 */
export async function fetchExtensions(params: ListParams): Promise<ListResponse> {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sort: params.sortBy,
    order: params.sortOrder,
  });

  if (params.search?.trim()) {
    queryParams.append('search', params.search.trim());
  }

  // Add domain filter if provided
  if (params.domainId) {
    queryParams.append('domainId', params.domainId.toString());
  }

  const response = await fetch(
    buildApiUrl(`/api/telephony/extensions?${queryParams}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch extensions');
  }

  const result = await response.json();
  return {
    data: result.data || [],
    pagination: {
      page: result.pagination?.page || params.page,
      limit: result.pagination?.limit || params.limit,
      total: result.pagination?.total || 0,
      totalPages: result.pagination?.totalPages || Math.ceil((result.pagination?.total || 0) / params.limit),
    },
  };
}

/**
 * Fetches a single extension by ID
 */
export async function fetchExtensionById(id: string | number): Promise<Extension> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/extensions/${id}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch extension');
  }

  const result = await response.json();
  // Handle cases where API returns { data: {...} } or {...} directly
  return result.data || result;
}

/**
 * Creates a new extension
 */
export async function createExtension(data: Record<string, unknown>): Promise<Extension> {
  const response = await fetch(
    buildApiUrl('/api/telephony/extensions'),
    {
      method: 'POST',
      headers: getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to create extension' }));
    throw new Error(errorData.message || 'Failed to create extension');
  }

  const result = await response.json();
  // Handle cases where API returns { data: {...} } or {...} directly
  return result.data || result;
}

/**
 * Updates an existing extension
 */
export async function updateExtension(id: string | number, data: Record<string, unknown>): Promise<Extension> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/extensions/${id}`),
    {
      method: 'PUT',
      headers: getApiHeaders(),
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Failed to update extension' }));
    throw new Error(errorData.message || 'Failed to update extension');
  }

  const result = await response.json();
  // Handle cases where API returns { data: {...} } or {...} directly
  // If no data is returned (update case), return the original data with the ID
  return result.data || result || { ...data, id: id as string };
}

/**
 * Deletes an extension by ID
 */
export async function deleteExtension(id: string | number): Promise<void> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/extensions/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    let message = 'Failed to delete extension';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors
    }
    throw new Error(message);
  }
}

/**
 * Fetches extension XML for a given extension ID
 */
export async function fetchExtensionXml(id: string | number): Promise<string> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/extensions/${id}/xml`),
    {
      headers: getApiHeaders()
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
  }

  return await response.text();
}

/**
 * Exports extensions as CSV
 */
export async function exportExtensions(): Promise<Blob> {
  const response = await fetch(
    buildApiUrl('/api/telephony/extensions/export'),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to export extensions');
  }

  return await response.blob();
}

/**
 * Fetches extension options (Class of Services, Agent Profiles, SIP Profiles)
 */
export async function fetchExtensionOptions(): Promise<ExtensionOptions> {
  // Fetch Class of Services (required)
  const cosResponse = await fetch(buildApiUrl('/api/telephony/class-of-services'), {
    headers: getApiHeaders(),
  }).catch(() => null);

  // Handle Class of Services response
  let cosResult: { success?: boolean; data?: unknown[] } = { success: false, data: [] };
  if (cosResponse && cosResponse.ok) {
    try {
      cosResult = await cosResponse.json();
    } catch (error) {
      console.warn('Failed to parse Class of Services response:', error);
    }
  } else if (!cosResponse) {
    console.warn('Failed to fetch Class of Services');
  }

  // Normalize classOfServices to ensure correct format.
  // NOTE: Sqids plugin encodes DB integer IDs as opaque alphanumeric strings (e.g. 'qdZW4AXl').
  // We must preserve the id as-is (string) instead of coercing to Number, which would give NaN
  // and cause the filter to drop every entry, leaving the dropdown empty.
  const normalizeClassOfServices = (data: unknown[]): Array<{ id: string | number; name: string }> => {
    if (!Array.isArray(data)) return [];
    return (data as Array<{ id?: string | number | null; name?: string; class_name?: string }>)
      .map((cos) => ({
        id: cos.id,
        name: cos.name || cos.class_name || String(cos.id || ''),
      }))
      .filter((cos): cos is { id: string | number; name: string } => cos.id != null && cos.id !== '' && cos.id !== 0 && !!cos.name);
  };

  // Fetch Dispatch Locations for E911 selector
  const dispatchResponse = await fetch(
    buildApiUrl('/api/telephony/dispatch-locations?limit=200&sortBy=locationName&sortOrder=asc'),
    { headers: getApiHeaders() }
  ).catch(() => null);

  let dispatchLocations: Array<{ id: number; locationName: string; locationCode: string | null }> = [];
  if (dispatchResponse && dispatchResponse.ok) {
    try {
      const dispatchResult = await dispatchResponse.json();
      // dispatch-locations API returns { data: [...], pagination: {...} } without a 'success' field
      if (Array.isArray(dispatchResult?.data)) {
        dispatchLocations = dispatchResult.data.map((d: { id: number; locationName: string; locationCode?: string | null }) => ({
          id: d.id,
          locationName: d.locationName,
          locationCode: d.locationCode || null,
        }));
      }
    } catch (error) {
      console.warn('Failed to parse Dispatch Locations response:', error);
    }
  }

    return {
    classOfServices: normalizeClassOfServices(cosResult.data || []),
    dialProfiles: [],
    agentProfiles: [], // Agent Profiles endpoint not available, return empty array
    sipProfiles: [], // Device SIP Profiles are loaded separately in useExtensionData
    smsNumbers: [], // SMS Numbers are loaded separately in useExtensionData
    faxDevices: [], // Fax Devices are no longer used (inline fax fields on extension)
    dispatchLocations,
    aiAgents: [], // AI Agents are loaded separately in useExtensionData
    aiTranslatorProfiles: [], // AI Translator Profiles are loaded separately in useExtensionData
  };
}

/**
 * Fetches system general settings for a domain
 */
export async function fetchSystemDefaults(domainId: number): Promise<unknown> {
  const response = await fetch(
    buildApiUrl(`/api/telephony/system-general/${domainId}`),
    {
      method: 'GET',
      headers: getApiHeaders(),
    }
  );

  if (!response.ok) {
    return null;
  }

  const result = await response.json();
  return result.success && result.data ? result.data : null;
}

/**
 * Fetches the next available extension number for a domain
 */
export async function fetchNextExtensionNumber(domainId?: number): Promise<string> {
  try {
    const url = domainId
      ? buildApiUrl(`/api/telephony/extensions/next-number?domainId=${domainId}`)
      : buildApiUrl('/api/telephony/extensions/next-number');
    const response = await fetch(url, {
      method: 'GET',
      headers: getApiHeaders(),
    });

    if (!response.ok) {
      return '';
    }

    const result = await response.json();
    return result.data?.nextExtension || '';
  } catch (error) {
    console.warn('Failed to fetch next extension number:', error);
    return '';
  }
}
